//src/components/PropertyImageUploader.js
import { useEffect, useMemo, useRef, useState } from "react";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_FILES = 10;

const PropertyImageUploader = ({
  images,
  onChange,
  error,
  disabled = false,
  maxFiles = MAX_FILES,
}) => {
  const inputRef = useRef(null);
  const [processing, setProcessing] = useState(false);

  const acceptedCount = useMemo(() => images?.length || 0, [images]);

  const revokePreview = (item) => {
    if (item?.preview) {
      URL.revokeObjectURL(item.preview);
    }
  };

  useEffect(() => {
    return () => {
      (images || []).forEach(revokePreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const openFilePicker = () => {
    if (disabled || processing) return;
    inputRef.current?.click();
  };

  const loadImage = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Gagal memuat gambar."));
      };

      img.src = objectUrl;
    });

  const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), type, quality);
    });

  const compressImage = async (file) => {
    if (!file.type.startsWith("image/")) {
      throw new Error("File harus berupa gambar.");
    }

    if (file.size <= MAX_IMAGE_SIZE) {
      return file;
    }

    const image = await loadImage(file);
    let width = image.naturalWidth || image.width;
    let height = image.naturalHeight || image.height;

    let quality = 0.85;
    let blob = null;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const currentWidth = Math.max(1, Math.round(width));
      const currentHeight = Math.max(1, Math.round(height));

      canvas.width = currentWidth;
      canvas.height = currentHeight;

      ctx.drawImage(image, 0, 0, currentWidth, currentHeight);

      blob = await canvasToBlob(canvas, "image/jpeg", quality);

      if (blob && blob.size <= MAX_IMAGE_SIZE) {
        break;
      }

      if (quality > 0.5) {
        quality -= 0.1;
      } else {
        width *= 0.88;
        height *= 0.88;
      }
    }

    if (!blob) {
      throw new Error("Gagal mengompres gambar.");
    }

    const compressedFile = new File(
      [blob],
      file.name.replace(/\.[^.]+$/, ".jpg"),
      {
        type: "image/jpeg",
        lastModified: Date.now(),
      },
    );

    return compressedFile;
  };

  const handleFiles = async (event) => {
    const fileList = Array.from(event.target.files || []);
    event.target.value = "";

    if (!fileList.length) return;

    if ((images?.length || 0) + fileList.length > maxFiles) {
      alert(`Maksimal ${maxFiles} foto properti.`);
      return;
    }

    setProcessing(true);

    try {
      const prepared = [];

      for (const file of fileList) {
        const finalFile = await compressImage(file);

        prepared.push({
          id: `${finalFile.name}-${finalFile.size}-${Date.now()}-${Math.random()
            .toString(16)
            .slice(2)}`,
          file: finalFile,
          preview: URL.createObjectURL(finalFile),
          name: finalFile.name,
          size: finalFile.size,
          type: finalFile.type,
        });
      }

      onChange([...(images || []), ...prepared]);
    } catch (err) {
      alert(err?.message || "Gagal memproses gambar.");
    } finally {
      setProcessing(false);
    }
  };

  const handleRemove = (id) => {
    const current = images || [];
    const target = current.find((item) => item.id === id);
    revokePreview(target);

    const next = current.filter((item) => item.id !== id);
    onChange(next);
  };

  return (
    <div className="sell-option-group">
      <label className="mp-label">Foto Properti</label>
      <p className="sell-spec-hint">
        Upload maksimal {maxFiles} foto. File di atas 2MB otomatis dikompres di
        frontend.
      </p>

      <div
        style={{
          border: "1px dashed var(--color-border)",
          borderRadius: "16px",
          padding: "18px",
          background: "rgba(255,255,255,0.55)",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFiles}
          style={{ display: "none" }}
          disabled={disabled || processing}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontWeight: 700, marginBottom: "4px" }}>
              Pilih foto properti
            </div>
            <div style={{ fontSize: "13px", color: "var(--color-text-muted)" }}>
              Format: JPG, PNG, WEBP. Ukuran otomatis dikompres bila perlu.
            </div>
          </div>

          <button
            type="button"
            className="btn-outline-mp"
            onClick={openFilePicker}
            disabled={disabled || processing}
            style={{ minWidth: "170px" }}
          >
            {processing ? "Memproses..." : "Tambah Foto"}
          </button>
        </div>

        {error && (
          <span
            className="sell-field-error"
            style={{ display: "block", marginTop: "10px" }}
          >
            {error}
          </span>
        )}

        {acceptedCount > 0 && (
          <div
            style={{
              marginTop: "14px",
              fontSize: "13px",
              color: "var(--color-text-muted)",
            }}
          >
            {acceptedCount} foto dipilih
          </div>
        )}

        {images?.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: "12px",
              marginTop: "16px",
            }}
          >
            {images.map((item) => (
              <div
                key={item.id}
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "14px",
                  overflow: "hidden",
                  background: "white",
                  position: "relative",
                }}
              >
                <div style={{ aspectRatio: "1 / 1", width: "100%" }}>
                  <img
                    src={item.preview}
                    alt={item.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>

                <div
                  style={{
                    padding: "10px",
                    fontSize: "12px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={item.name}
                  >
                    {item.name}
                  </div>
                  <div
                    style={{
                      color: "var(--color-text-muted)",
                      marginTop: "4px",
                    }}
                  >
                    {(item.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRemove(item.id)}
                  disabled={disabled || processing}
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    border: "none",
                    background: "rgba(0,0,0,0.65)",
                    color: "white",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    lineHeight: 1,
                  }}
                  aria-label="Hapus foto"
                  title="Hapus foto"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyImageUploader;
